import { FC, useRef, useMemo, useState, useEffect } from 'react';
import { useMethods } from '@lhb/hook';
import { isNotEmptyAny, isArray, refactorPermissions } from '@lhb/func';
import { getChancePointDetail, chancePointRecycleDetail } from '@/common/api/expandStore/chancepoint';
import { ChanceDetailPermission, IS_DETAIL_EDIT_CHANCE } from '@/common/components/business/ExpandStore/ts-config';
import { hasAreaProtectionAndAddress } from '@/common/components/business/DynamicComponent/config';
import { Spin } from 'antd';
import Title from './components/Title';
import V2Container from '@/common/components/Data/V2Container';
import { ModelSyncOptions } from '../modelSync';
import DetailModules from './components/DetailModules';

/**
 * 机会点详情内容
 * @description
 * @demo
 */
const Component:FC<{
  id,
  onClose?,
  /** 编辑 */
  setFormDrawerData?,
  /** 刷新机会点详情的标识，如果变更就触发机会点刷新 */
  refreshDetail?,
  /** 表单支持直接编辑的时候时候返回刷新列表, 用于刷新数据，如提交审批，保存等 */
  onSearch?,
  /** 刷新列表当前页 */
  onSearchCurrentPage?,
  /** 上传文件更新 */
  updateHandle?,
  /** 隐藏操作按钮 */
  hideOperate?,
  /** 禁用匹配商圈点击详情 */
  disabledArea?,
  /** 是否废弃机会点 */
  isRecycle?,
  /** 分享时调用接口需要的数据，如果有值代表“分享时” */
  shareData?: { tenantId: string, userId: string } | null,
}> = ({
  id,
  onClose,
  setFormDrawerData, // 编辑
  refreshDetail,
  onSearch, // 表单支持直接编辑的时候时候返回刷新列表, 用于刷新数据，如提交审批，保存等
  onSearchCurrentPage, // 刷新列表当前页
  updateHandle, // 上传文件更新
  hideOperate = false, // 隐藏操作按钮
  disabledArea = false, // 禁用匹配商圈点击详情
  isRecycle = false, // 是否废弃机会点
  shareData = null, // 分享时调用接口需要的数据，如果有值代表“分享时”
}) => {

  /* state */
  const chanceFormRef = useRef(null);
  const titleHeightRef = useRef(0);
  const [hintStr, setHintStr] = useState<string>('');
  const [hasCollisionWithin200m, setHasCollisionWithin200m] = useState<boolean | undefined>(false);

  const [detail, setDetail] = useState<any>();
  const [loading, setLoading] = useState<any>(true);
  const hasAreaProtection = useMemo(() => {
    const infoModule = isArray(detail?.moduleDetails) ? detail.moduleDetails.find((item: any) => item.moduleType === 4)?.infoModule : undefined;
    return hasAreaProtectionAndAddress(infoModule?.propertyGroupVOList);
  }, [detail]);
  const antiCollisionApprovalStatus = hasAreaProtection ? detail?.antiCollisionApprovalInfo?.approvalStatus : undefined;

  /**
   * @description 是否可编辑机会点详情表单
   */
  const canEditForm = useMemo(() => {
    // 详情为空 hideOperate手动控制不可以编辑
    if (!isNotEmptyAny(detail) || hideOperate) return false;
    // 防撞店特殊申请审核中时，详情中的所有字段均不可编辑。
    if (antiCollisionApprovalStatus === 0) return false;

    const { permissions, supportDirectApproval } = detail;
    // 功能未启用、或者不支持审批流
    if (!IS_DETAIL_EDIT_CHANCE || supportDirectApproval !== 1 || !isArray(permissions)) return false;

    const refactorPerms = refactorPermissions(permissions);

    // 机会点详情同时有edit和submitApproval权限时
    const editTarget = refactorPerms.find((itm: any) => (itm.event === ChanceDetailPermission.Edit));
    const approvalTarget = refactorPerms.find((itm: any) => (itm.event === ChanceDetailPermission.SubmitApprove));
    return !!editTarget && !!approvalTarget;
  }, [detail, antiCollisionApprovalStatus]);

  /* hooks */
  useEffect(() => {
    methods.getDetail();
  }, [refreshDetail]);

  /* methods */
  const methods = useMethods({

    init() {
      methods.getDetail();
    },

    getDetail: async() => {
      if (!id) {
        return;
      }
      const api = isRecycle ? chancePointRecycleDetail : getChancePointDetail;
      const params = isRecycle ? { chancePointId: id } : { id };

      setLoading(true);
      api(params).then(res => {
        const infoModule = isArray(res?.moduleDetails) ? res.moduleDetails.find((item: any) => item.moduleType === 4)?.infoModule : undefined;
        const hasAreaProtectionInDetail = hasAreaProtectionAndAddress(infoModule?.propertyGroupVOList);

        // 配置区域保护时，等待检测结果确认后才开放提交审批入口。
        setHasCollisionWithin200m(hasAreaProtectionInDetail ? undefined : false);
        setDetail(res);
      }).finally(() => {
        setLoading(false);
      });
    },

    /** 提交审批时保存机会点 */
    saveHandle: (needCheck: boolean, cb: Function, shouldRefreshAfterSave = true) => {
      // 调用保存机会点
      (chanceFormRef?.current as any)?.saveChance(needCheck, cb, shouldRefreshAfterSave);
    },
    /** 触发一鸣指定字段的关联计算并保存。 */
    updateModule(onComplete?: (success: boolean) => void, options?: ModelSyncOptions) {
      if (!(chanceFormRef?.current as any)?.updateModule) {
        onComplete?.(false);
        return;
      }
      (chanceFormRef.current as any).updateModule(onComplete, options);
    },
    handleAreaProtectionCollisionChange(value: boolean | undefined) {
      setHasCollisionWithin200m(value);
    },

  });

  return (<>
    <Spin spinning={loading}>
      <V2Container
        extraContent={{
          top: <Title
            titleHeightRef={titleHeightRef}
            detail={detail}
            hintStr={hintStr}
            setFormDrawerData={setFormDrawerData}
            canEditForm={canEditForm}
            onClose={() => onClose?.()}
            updateHandle={updateHandle}
            onSearch={onSearch}
            onSearchCurrentPage={onSearchCurrentPage}
            saveHandle={methods.saveHandle}
            updateModule={methods.updateModule}
            hasCollisionWithin200m={hasCollisionWithin200m}
            antiCollisionApprovalStatus={antiCollisionApprovalStatus}
            onDetailRefresh={methods.getDetail}
            hideOperate={hideOperate}
            isRecycle={isRecycle}
          />
        }}
        style={{ height: '100vh' }}
      >
        <DetailModules
          ref={chanceFormRef}
          titleHeightRef={titleHeightRef}
          detail={detail}
          isScrollContainer={true}
          canEditForm={canEditForm}
          onSearch={onSearch}
          setHintStr={setHintStr}
          updateHandle={updateHandle}
          wrapperClassName='pt-16 pl-40 pr-40'
          disabledArea={disabledArea}
          shareData={shareData}
          antiCollisionApprovalStatus={antiCollisionApprovalStatus}
          onAreaProtectionCollisionChange={methods.handleAreaProtectionCollisionChange}
          setLoading={setLoading}
        />
      </V2Container>
    </Spin>
  </>);
};

export default Component;
